import { Box } from '@frontend/legacy-components';

import { useDxfViewerSession } from '../DxfViewerContext';
import { DXF_Z_INDEX } from '../constants';
import { useDxfBrushCursorBadge } from '../hooks/useDxfBrushCursorBadge';

/**
 * The small chip that trails the cursor while a role brush is armed, over
 * paintable geometry or a non-empty paint-marquee — see
 * `useDxfBrushCursorBadge` for why it is positioned imperatively rather than
 * through React state.
 *
 * Mounted unconditionally, like `DxfMappingMenu`'s sibling tool layers: the
 * hook itself hides the chip whenever no brush is armed, so there is no
 * `isOpen` gate to duplicate here.
 */
export const DxfBrushCursorBadge = () => {
  const session = useDxfViewerSession();
  const badgeRef = useDxfBrushCursorBadge({
    pointer: session.pointer,
    tool: session.tools,
    selection: session.selection,
    marqueePreview: session.marqueePreview,
  });

  return (
    <Box
      ref={badgeRef}
      position="absolute"
      top={0}
      left={0}
      zIndex={DXF_Z_INDEX.FLOATING}
      // Placed and revealed by the hook; hidden here so it never flashes at
      // the container's top-left corner on the frame it mounts.
      visibility="hidden"
      color="page.surface"
      borderRadius="md"
      px={1.5}
      py={0.5}
      fontSize="xs"
      fontWeight="bold"
      whiteSpace="nowrap"
      pointerEvents="none"
      userSelect="none"
      data-testid="dxf-brush-cursor-badge"
    />
  );
};
DxfBrushCursorBadge.displayName = 'DxfBrushCursorBadge';
